import { Collections, TypeFromDocument } from '@/types/TypeFromCollection';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import firestore from '../firestore';
import { handleUnauthenticatedError } from '../handleUnauthenticatedError';

const isDev = process.env.NODE_ENV === 'development';

const logUseDocumentTiming = (context: {
  mode: 'getDoc' | 'onSnapshot:first' | 'onSnapshot:error' | 'getDoc:error';
  collectionName: string;
  documentId: string;
  ms: number;
}) => {
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.log('[useDocument]', context);
};

/**
 The useDocument hook is used to get real-time data from a Firestore document.
 The data is retrieved as an object.
 The function returns the typed data, loading status, and error.
 @param collectionName The name of the collection.
 @param documentId The id of the document.
 @param subscribe Whether to subscribe to the document or not.
**/
export function useDocument<C extends Collections>(
  collectionName: C,
  documentId?: string,
  subscribe = false
) {
  const [data, setData] = useState<TypeFromDocument<C> | null>(null);
  const [loading, setLoading] = useState(!!documentId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) return;

    const startedAt = Date.now();
    const documentData = doc(firestore, collectionName, documentId);

    if (!subscribe) {
      // eslint-disable-next-line no-console
      setLoading(true);
      getDoc(documentData)
        .then((snapshot) => {
          if (loading) setLoading(false);
          if (!snapshot.exists()) {
            setData(null);
            return;
          }
          setLoading(false);
          setData({ ...snapshot.data(), id: snapshot.id } as TypeFromDocument<C>);
          logUseDocumentTiming({
            mode: 'getDoc',
            collectionName,
            documentId,
            ms: Date.now() - startedAt
          });
        })
        .catch((e) => {
          if (loading) setLoading(false);
          setError(e.message);
          logUseDocumentTiming({
            mode: 'getDoc:error',
            collectionName,
            documentId,
            ms: Date.now() - startedAt
          });
          handleUnauthenticatedError(e);
        });
      return;
    }

    let loggedFirstSnapshot = false;
    const unsubscribe = onSnapshot(
      documentData,
      (snapshot) => {
        if (!loggedFirstSnapshot) {
          loggedFirstSnapshot = true;
          logUseDocumentTiming({
            mode: 'onSnapshot:first',
            collectionName,
            documentId,
            ms: Date.now() - startedAt
          });
        }
        if (loading) setLoading(false);
        if (!snapshot.exists()) {
          setData(null);
          return;
        }
        setData({ ...snapshot.data(), id: snapshot.id } as TypeFromDocument<C>);
      },
      (e) => {
        if (loading) setLoading(false);
        setError(e.message);
        logUseDocumentTiming({
          mode: 'onSnapshot:error',
          collectionName,
          documentId,
          ms: Date.now() - startedAt
        });
        handleUnauthenticatedError(e);
      }
    );
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, documentId, subscribe]);

  return { data, loading, error };
}
